/**
 * The language-model actions (SEO_BRIEF_09 Part C, contracts/api.md 1.19.0): translate a
 * transcript, summarize it, write post captions from it, and translate a subtitle file's lines.
 *
 * Every one is the same exchange. The POST answers at once: `200 {"status":"done","result"}`
 * when the answer is cached, or `202 {"status":"queued"|"running","task_id"}` when the server
 * has started the work, which is then polled at `GET /api/gemini/tasks/{task_id}` until it is
 * `done` or `failed`. The caller gets the result, or one of two errors:
 *
 * - `GeminiLimitError` on `429 GEMINI_LIMIT`: the model can't take the request until `resetsAt`
 *   (brief 2.2). `limitMessage()` writes the sentence; `limit.fallback` is set only for a
 *   translation into English of a link, which can go through speech recognition instead.
 * - `ApiError` for everything else, with its problem (lib/api.ts).
 *
 * `AT_CAPACITY` (the server's queue is full, or the per-minute pacer waited too long) is not
 * handed to the caller while there is time left: the request is made again after `Retry-After`,
 * with `queued()` called so the page can say "Queued, starting in a few seconds".
 *
 * The live translator's `liveTranslate` (SEO_BRIEF_10, contracts/api.md 1.22.0 §3.12) is the
 * exception: one sentence, one POST answered within seconds, no task and no poll, and an
 * `AT_CAPACITY` handed straight to the caller, which asks once more and then moves on.
 */

import { ApiError, request } from './api.ts';
import type { Problem } from './api-types.ts';
import { type PageLang, pageLang, t } from './i18n.ts';

export type GeminiScope = 'global' | 'visitor';

/** The `GEMINI_LIMIT` problem's extensions, as the page needs them. */
export interface GeminiLimit {
  /** `translate` | `subtitles` | `summary` | `captions` | `live`. */
  feature: string;
  scope: GeminiScope;
  /** Epoch seconds: the next midnight Pacific, when the allowance comes back. */
  resetsAt: number;
  /** The per-visitor cap that refused (scope `visitor`); off by default and never shown. */
  cap: number;
  /** Translate into English of a link only: the same link through speech recognition. */
  fallback: null | { kind: 'whisper'; minutes: number; url: string };
  /**
   * The live translator into English only (`fallback: {kind: "hold_to_talk"}` on the wire):
   * Hold to talk can still translate it, so the page offers it beside the limit sentence.
   */
  holdToTalk: boolean;
}

/** What a caller hears while it waits. Each is called at most once per state change. */
export interface TaskEvents {
  queued?(): void;
  running?(): void;
  signal?: AbortSignal;
}

export interface TranslateDone {
  /** The translation's own `/t/{public_id}`; the page navigates there. */
  public_id: string;
  target: string;
  /** The language translated from, null when the original's language was unknown. */
  source: string | null;
  /** Segment indexes left in the original language. */
  missing: number[];
  cached: boolean;
}

export interface SummaryKeyPoint {
  /** Seconds from the start of the media. */
  s: number;
  text: string;
}

export interface SummaryDone {
  summary: string;
  key_points: SummaryKeyPoint[];
  lang: string | null;
  /** Set when the input was cut at the token cap: the seconds the summary covers. */
  covers_s: number | null;
  cached: boolean;
}

export interface CaptionText {
  kind: 'short' | 'medium' | 'long';
  text: string;
  chars: number;
}

export interface CaptionsDone {
  platform: string;
  /** The platform's caption limit, in characters. */
  limit: number;
  captions: CaptionText[];
  hashtags: string[];
  cached: boolean;
}

export interface SubtitleLine {
  i: number;
  t: string;
}

export interface SubtitlesDone {
  cues: SubtitleLine[];
  /** Cue ids left in the original language. */
  missing: number[];
  cached: boolean;
}

/** Thrown on `429 GEMINI_LIMIT`. Still an `ApiError`, so a generic handler can show its state. */
export class GeminiLimitError extends ApiError {
  readonly limit: GeminiLimit;
  constructor(problem: Problem, limit: GeminiLimit) {
    super(problem);
    this.name = 'GeminiLimitError';
    this.limit = limit;
  }
}

/* ── the exchange ─────────────────────────────────────────────────────── */

type TaskStatus = 'queued' | 'running' | 'done' | 'failed';

interface TaskAnswer<T> {
  status: TaskStatus;
  task_id?: string;
  result?: T;
  problem?: Problem;
}

/** First poll after this long; each later one waits a little longer, up to POLL_MAX_MS. */
const POLL_FIRST_MS = 1_500;
const POLL_MAX_MS = 5_000;
const POLL_GROWTH = 1.25;
/** Past this the caller is told it timed out; the work may still finish and be cached. */
const GIVE_UP_MS = 180_000;
/** A 30 s Gemini call plus the per-minute pacer's wait fits; the POST itself answers at once. */
const POST_TIMEOUT_MS = 30_000;
/** AT_CAPACITY without a usable Retry-After waits this long before asking again. */
const BUSY_RETRY_S = 5;

function timeoutProblem(): Problem {
  return {
    type: 'about:blank#timeout',
    title: 'TIMEOUT',
    status: 0,
    detail: 'the task did not finish in time',
    code: 'TIMEOUT',
    state: null,
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** The problem's extensions read into a `GeminiLimit`; null for any other problem. */
export function readLimit(problem: Problem): GeminiLimit | null {
  if (problem.code !== 'GEMINI_LIMIT') return null;
  const ext = problem as unknown as Record<string, unknown>;
  const raw = ext['fallback'];
  let fallback: GeminiLimit['fallback'] = null;
  let holdToTalk = false;
  if (typeof raw === 'object' && raw !== null) {
    const f = raw as Record<string, unknown>;
    if (f['kind'] === 'whisper' && typeof f['url'] === 'string') {
      fallback = { kind: 'whisper', minutes: numberOr(f['minutes'], 1), url: f['url'] };
    }
    holdToTalk = f['kind'] === 'hold_to_talk';
  }
  return {
    feature: typeof ext['feature'] === 'string' ? ext['feature'] : 'translate',
    scope: ext['scope'] === 'visitor' ? 'visitor' : 'global',
    resetsAt: numberOr(ext['resets_at'], Math.floor(Date.now() / 1000) + 3_600),
    cap: numberOr(ext['cap'], 10),
    fallback,
    holdToTalk,
  };
}

function raise(problem: Problem): never {
  const limit = readLimit(problem);
  if (limit) throw new GeminiLimitError(problem, limit);
  throw new ApiError(problem);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function busySeconds(problem: Problem): number {
  const seconds = problem.retry_after_s;
  return typeof seconds === 'number' && seconds > 0 ? Math.min(seconds, 30) : BUSY_RETRY_S;
}

/**
 * POST, then poll until done. `AT_CAPACITY`, from the POST or from a failed task, is retried
 * after its wait while there is time left; any other refusal is thrown at once.
 */
async function run<T>(path: string, body: unknown, on: TaskEvents = {}): Promise<T> {
  const deadline = Date.now() + GIVE_UP_MS;
  const signal = on.signal;
  let told: TaskStatus | null = null;
  const tell = (status: TaskStatus) => {
    if (status === told) return;
    told = status;
    if (status === 'queued') on.queued?.();
    else if (status === 'running') on.running?.();
  };

  for (;;) {
    let answer: TaskAnswer<T>;
    try {
      answer = await request<TaskAnswer<T>>(path, {
        method: 'POST',
        body,
        timeoutMs: POST_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && err.problem.code === 'AT_CAPACITY') {
        const wait = busySeconds(err.problem) * 1000;
        if (Date.now() + wait < deadline) {
          tell('queued');
          await sleep(wait, signal);
          continue;
        }
      }
      if (err instanceof ApiError) raise(err.problem);
      throw err;
    }

    if (answer.status === 'done' && answer.result !== undefined) return answer.result;
    if (answer.status === 'failed' && answer.problem) raise(answer.problem);
    if (typeof answer.task_id !== 'string') {
      throw new ApiError({ ...timeoutProblem(), code: 'BAD_RESPONSE', title: 'BAD_RESPONSE' });
    }

    const busy = await follow<T>(answer.task_id, answer.status, deadline, tell, signal);
    if (busy.done) return busy.result;
    // The task failed AT_CAPACITY: ask again after its wait, while there is time.
    const wait = busySeconds(busy.problem) * 1000;
    if (Date.now() + wait >= deadline) raise(busy.problem);
    tell('queued');
    await sleep(wait, signal);
  }
}

type Followed<T> = { done: true; result: T } | { done: false; problem: Problem };

/** Poll one task to its end. A failed task's AT_CAPACITY comes back to `run`; the rest throw. */
async function follow<T>(
  taskId: string,
  first: TaskStatus,
  deadline: number,
  tell: (status: TaskStatus) => void,
  signal?: AbortSignal,
): Promise<Followed<T>> {
  const path = `/api/gemini/tasks/${encodeURIComponent(taskId)}`;
  let status = first;
  let wait = POLL_FIRST_MS;
  for (;;) {
    tell(status);
    if (Date.now() + wait >= deadline) throw new ApiError(timeoutProblem());
    await sleep(wait, signal);
    wait = Math.min(POLL_MAX_MS, Math.round(wait * POLL_GROWTH));
    const answer = await request<TaskAnswer<T>>(path, signal ? { signal } : {});
    if (answer.status === 'done' && answer.result !== undefined) {
      return { done: true, result: answer.result };
    }
    if (answer.status === 'failed') {
      const problem = answer.problem ?? { ...timeoutProblem(), code: 'INTERNAL_ERROR' };
      if (problem.code === 'AT_CAPACITY') return { done: false, problem };
      raise(problem);
    }
    status = answer.status;
  }
}

/* ── the four actions ─────────────────────────────────────────────────── */

const transcriptPath = (publicId: string, action: string) =>
  `/api/transcripts/${encodeURIComponent(publicId)}/${action}`;

/**
 * Translate a transcript (the Translate picker, or the "Translate to English" button). A
 * translation row is translated from its original. The result names the translation's own
 * `/t/` page.
 */
export function translateTranscript(
  publicId: string,
  target: string,
  trigger: 'picker' | 'en_button',
  on?: TaskEvents,
): Promise<TranslateDone> {
  return run<TranslateDone>(transcriptPath(publicId, 'translate'), { target, trigger }, on);
}

/** A short summary and up to 8 key points, in `lang` or the transcript's own language. */
export function summarizeTranscript(
  publicId: string,
  lang: string | null,
  on?: TaskEvents,
): Promise<SummaryDone> {
  return run<SummaryDone>(transcriptPath(publicId, 'summary'), { lang }, on);
}

/** Three post captions and hashtags for `platform`, each within its caption limit. */
export function generateCaptions(
  publicId: string,
  platform: string,
  lang: string | null,
  on?: TaskEvents,
): Promise<CaptionsDone> {
  return run<CaptionsDone>(transcriptPath(publicId, 'captions'), { platform, lang }, on);
}

/**
 * The server's limits for one subtitle file (contracts/api.md §3.11.4): past either it answers
 * `413 SUBTITLES_TOO_LARGE`, so a page can say so before sending anything. Text is counted in
 * UTF-8 bytes (`new TextEncoder().encode(t).length`), blank lines included.
 */
export const SUBTITLE_LIMITS = { cues: 5_000, textBytes: 2 * 1024 * 1024 } as const;

/**
 * A subtitle file's lines, translated. Only the cue texts and their ids are sent: the browser
 * keeps the timings and puts each line back on its cue.
 */
export function translateSubtitles(
  body: { target: string; source: string | null; format: string; cues: SubtitleLine[] },
  on?: TaskEvents,
): Promise<SubtitlesDone> {
  return run<SubtitlesDone>('/api/subtitles/translate', body, on);
}

/* ── the live translator (SEO_BRIEF_10, contracts/api.md §3.12) ─────────── */

/**
 * The most one live sentence may be, in characters once trimmed: past it the server answers
 * `413 LIVE_TEXT_TOO_LONG`, so the page splits a long final at punctuation first. A string's
 * `length` counts UTF-16 units, never fewer than the server's code points, so a piece within
 * this by `length` is within it on the server too.
 */
export const LIVE_MAX_CHARS = 300;

/** The server gives one sentence about 8 s, then answers `503 AT_CAPACITY`; this is past that. */
const LIVE_TIMEOUT_MS = 12_000;

export interface LiveDone {
  /** The translation: one sentence back for the one sent. */
  text: string;
  /** The same sentence was translated recently, and this answer cost nothing. */
  cached: boolean;
}

/**
 * One finished sentence, translated from `source` into `target` (base codes: `en`, `es`, `zh`
 * for Simplified Chinese, `zh-Hant`): `POST /api/live-translate`, a single request with no task
 * and no poll. Throws, as the other actions do:
 *
 * - `GeminiLimitError` on `429 GEMINI_LIMIT`: `limit.feature` is `live`, and `limit.holdToTalk`
 *   is true when the target is English, which Hold to talk can still translate.
 * - `ApiError` for everything else. `AT_CAPACITY` (busy, or the sentence's few seconds ran out)
 *   carries `problem.retry_after_s`; it is never retried here, so the page asks once more and
 *   then shows the original. Also `LIVE_TEXT_TOO_LONG`, `ALREADY_IN_LANGUAGE`, `BAD_REQUEST`,
 *   and the client's own `TIMEOUT` and `NETWORK`.
 *
 * An abort through `signal` rejects with the signal's reason, as every request here does.
 */
export async function liveTranslate(
  text: string,
  source: string,
  target: string,
  signal?: AbortSignal,
): Promise<LiveDone> {
  signal?.throwIfAborted();
  let answer: Partial<LiveDone> | undefined;
  try {
    answer = await request<Partial<LiveDone>>('/api/live-translate', {
      method: 'POST',
      body: { text, source, target },
      timeoutMs: LIVE_TIMEOUT_MS,
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (err instanceof ApiError) raise(err.problem);
    throw err;
  }
  if (typeof answer?.text !== 'string') {
    throw new ApiError({
      ...timeoutProblem(),
      code: 'BAD_RESPONSE',
      title: 'BAD_RESPONSE',
      detail: 'the answer carries no text',
    });
  }
  return { text: answer.text, cached: answer.cached === true };
}

/* ── the limit message (brief 2.2) ─────────────────────────────────────── */

/** "4:00 PM", "16:00": the visitor's local time of `epochS`, in the page's language. */
export function resetTime(epochS: number, lang: PageLang = pageLang()): string {
  const locale = lang === 'en' ? undefined : lang;
  try {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(epochS * 1000),
    );
  } catch {
    return new Date(epochS * 1000).toLocaleTimeString();
  }
}

/**
 * What the page says when the language model can't take a request: "Translation and summaries
 * aren't available right now. They'll be back by 4:00 PM. Transcription still works." The same
 * words for the spent day and for a visitor's cap (scope `visitor`, only when one is switched on
 * by env): the founder's ruling of 2026-10-26 is that a visitor is never told of a limit.
 */
export function limitMessage(limit: GeminiLimit, lang: PageLang = pageLang()): string {
  const first = t("Translation and summaries aren't available right now.", lang);
  const back = t("They'll be back by {reset}.", lang).replace(
    '{reset}',
    resetTime(limit.resetsAt, lang),
  );
  return `${first} ${back} ${t('Transcription still works.', lang)}`;
}
